import { EVA_RESEARCH_NAME } from "constants/app.constants";
import {
  MCDANIEL_SEARCH_FOLDER_NAME,
  MY_FOCUS_FIELDS,
  ORG_FOCUS_FIELDS,
  PROJECT_FOCUS_FIELDS
} from "constants/settings.constants";
// Imported from its own file, not the `utils` barrel: the barrel re-exports
// `utils/chart`, which (as of EVA-127) imports stripFieldContext from this
// file — going through the barrel here would close that into a real import
// cycle (utils -> utils/chart -> this file -> utils -> ...).
import getCumPlusForecastTitle from "utils/chart/getCumPlusForecastTitle";
import { isNormalizedProductionSummary } from "utils/column/isNormalizedProductionSummary";
import { getPerformanceRatioDisplayName } from "utils/data-table/getProductDisplayName";

import {
  IGroupByListColumn,
  INormalizeBySetting,
  isShapefileBoundaryProperty
} from "models";
import { ColumnLabelContext } from "models/columns";
import { EntityKind } from "models/entityKind";

import { getProductLabel } from "components/charts/utils/getProductLabel";
import { formatPerformanceDateRange } from "components/groupBy/helpers";
import {
  geoVolumeCategoryTitle,
  padMetricsCategoryTitle
} from "components/groupBy/hooks/useCategories";

/**
 * Custom (focus) fields identify by property prefix — `group` is often empty.
 * Midstream (facility) custom fields carry their own prefixes but the same
 * categories as upstream (see `CUSTOM_FIELD_CATEGORY_BY_PREFIX` in useProps).
 */
export const isCustomFieldProperty = (property: string): boolean =>
  property.startsWith("Org_Focus_Fields") ||
  property.startsWith("Org_Midstream_Fields") ||
  property.startsWith("My_Focus_Fields") ||
  property.startsWith("My_Midstream_Fields") ||
  property.startsWith("Project_Focus_Fields");

/**
 * Org geo columns (Geo Calculations and Geo Simulations — see
 * `CUSTOM_FIELD_CATEGORY_BY_PREFIX` in `components/groupBy/hooks/useProps.tsx`)
 * are the ONLY custom fields whose title is known to repeat the subgroup — see
 * `stripSubgroupFromTitle`. Every other custom (and shapefile) field's title is
 * user-authored, and a substring match against the subgroup there would
 * silently truncate a legitimately-named field (e.g. "Debug - v2" in subgroup
 * "Debug" losing "Debug - "). A `stripSubgroupFromTitle` caller that can see
 * OTHER field types must gate on this before calling it — every call site in
 * this file does. A caller whose input is structurally already org-geo-only
 * (e.g. `OrgGeoCategory.tsx` / `OrgGeoSimsCategory.tsx`, which only ever
 * iterate org geo columns to begin with) doesn't need the gate — the
 * invariant holds by construction, not by a runtime check — but should say so
 * in a comment, since it isn't otherwise obvious from the call site.
 */
export const isOrgGeoProperty = (property: string): boolean =>
  property.startsWith("Org_Focus_Fields_Geo_") ||
  property.startsWith("Org_Focus_Fields_Sim_Geo_");

/**
 * Picks the right label variant for a flat, single-field display (focus tiles,
 * tooltip-default settings, map tooltip). Custom fields need their group name
 * to disambiguate, which only lives in fullContextTitle — every other field
 * type already carries what it needs in lightContextTitle (and omitting the
 * product/group prefix keeps those compact).
 */
export const getFlatDisplayTitle = (
  property: string,
  labels: ColumnLabelContext,
  fallback = ""
): string => {
  const preferred = isCustomFieldProperty(property)
    ? labels.fullContextTitle
    : labels.lightContextTitle;
  return preferred || fallback;
};

/**
 * Builds the qualified prefix for a custom (focus) field: `Type: Group`, where
 * Type is the category (derived from the property prefix, since `group` can be
 * empty on the raw column) and Group is the user-defined group name (`subgroup`).
 * The group is omitted when absent so fields without a group read as `Type`.
 */
export const getCustomFieldGroupPrefix = (property: {
  property: string;
  group?: string;
  subgroup?: string;
}): string => {
  let category = property.group;
  if (
    property.property.startsWith("My_Focus_Fields") ||
    property.property.startsWith("My_Midstream_Fields")
  ) {
    category = MY_FOCUS_FIELDS;
  } else if (
    property.property.startsWith("Org_Focus_Fields") ||
    property.property.startsWith("Org_Midstream_Fields")
  ) {
    category = ORG_FOCUS_FIELDS;
  } else if (property.property.startsWith("Project_Focus_Fields")) {
    category = PROJECT_FOCUS_FIELDS;
  }
  return [category, property.subgroup].filter(Boolean).join(": ");
};

export const FLUID_ANALYSIS_GROUP = "Fluid Analysis";
const FLUID_TEST_METADATA_KEY = "first/Last Test";

// Optional group/subgroup (unlike IGroupByListColumn, where they're required) so this type
// also accepts IGroupBy — the shape the map/chart legend title hooks hold their active
// group-by in when calling decorateTitleWithFieldContext / stripFieldContext directly.
type FieldContextColumn = Pick<
  IGroupByListColumn,
  "property" | "metadata" | "defaultUnit"
> & {
  group?: string;
  subgroup?: string;
};

/**
 * Single source of truth for the context PREFIX a field type carries (the part
 * that goes before the title): custom fields → "Type: Group", shapefile fields
 * → the shapefile name. Empty for every other type.
 */
export const getFieldContextPrefix = (column: FieldContextColumn): string => {
  if (!column?.property) return "";
  if (isCustomFieldProperty(column.property)) {
    return getCustomFieldGroupPrefix(column);
  }
  // The boundary focus displays as the shapefile's name (updateGroupBy.ts) —
  // no separate prefix, or a boundary column reads "Basins: Basins".
  if (
    column.property.startsWith("Shapefile_") &&
    !isShapefileBoundaryProperty(column.property)
  ) {
    return column.subgroup ?? "";
  }
  return "";
};

/**
 * Single source of truth for the context SUFFIX tokens a field type carries
 * (each rendered as " (token)"): geo/pad-metrics → [radius, source], Fluid
 * Analysis → [First/Last Test]. Empty for every other type.
 */
export const getFieldContextSuffixTokens = (column: FieldContextColumn): string[] => {
  if (!column?.property) return [];
  if (
    column.group === geoVolumeCategoryTitle ||
    column.group === padMetricsCategoryTitle
  ) {
    return [column.metadata?.radius, column.metadata?.source].filter(Boolean) as string[];
  }
  if (column.group === FLUID_ANALYSIS_GROUP) {
    const test = column.metadata?.[FLUID_TEST_METADATA_KEY];
    return test ? [`${test} Test`] : [];
  }
  return [];
};

/**
 * Single source of truth for the context PREFIX a legend/axis label carries —
 * distinct from `getFieldContextPrefix`, which the disambiguation surfaces
 * (focus panel, XDA picker, predicate tree) use. Custom fields → subgroup
 * ONLY (the category, e.g. "Organization Fields", must never reach a legend
 * or axis — that's the EVA-6338 regression this file fixes). Shapefile
 * fields → the shapefile name (no separate category concept there, so it's
 * unchanged from `getFieldContextPrefix`). Empty for every other type.
 *
 * Note: shapefile fields go through here on every `applyFieldNameMode` path
 * (the "Simplify Custom Field Names" toggle is scoped to custom fields only —
 * see `applyFieldNameMode` — so this subgroup prefix is never stripped from
 * a shapefile field regardless of toggle state).
 */
export const getLegendFieldPrefix = (column: FieldContextColumn): string => {
  if (!column?.property) return "";
  if (isCustomFieldProperty(column.property)) {
    return column.subgroup ?? "";
  }
  // See getFieldContextPrefix — the boundary focus is titled after the
  // shapefile itself, so it must not also be prefixed with it.
  if (
    column.property.startsWith("Shapefile_") &&
    !isShapefileBoundaryProperty(column.property)
  ) {
    return column.subgroup ?? "";
  }
  return "";
};

/**
 * Org geo columns (Geo Calculations and Geo Simulations) arrive from the
 * backend with the job name already baked into the title — job "test again"
 * yields "test again - 3DV Unconstrained". Every label variant here also
 * prefixes the subgroup (which IS the job name), so the job showed up twice:
 * "Organization Fields: test again: test again - 3DV Unconstrained (ha-m)".
 *
 * Strip the exact `{subgroup} - ` lead-in so the prefix stays the only copy.
 * Matched against the subgroup rather than the first " - " (what the focus
 * panel used to do locally) so a field legitimately containing " - " keeps
 * its name. Idempotent, and a no-op for every column whose title doesn't
 * repeat its subgroup.
 */
export const stripSubgroupFromTitle = (title: string, subgroup?: string): string =>
  subgroup && title?.startsWith(`${subgroup} - `)
    ? title.slice(subgroup.length + 3)
    : title;

/**
 * Decorates an already-rendered title (e.g. a chart-service axis title that
 * already carries the unit) with the same field context the focus panel and
 * legend show. Prepends/appends only what's missing (idempotent) using the
 * shared prefix/suffix tokenizers, and leaves every other field type untouched,
 * so it can safely run over a backend-provided axis label without disturbing
 * its unit or normalization handling.
 */
export const decorateTitleWithFieldContext = (
  baseTitle: string,
  column: FieldContextColumn
): string => {
  if (!baseTitle || !column?.property) return baseTitle;

  // No org-geo subgroup strip here (unlike stripFieldContext/applyFieldNameMode
  // below): this function's one production caller, applyFieldNameMode, only
  // reaches it from the `!isCustomFieldProperty` branch — and isOrgGeoProperty
  // is a subset of isCustomFieldProperty (both org-geo prefixes start with
  // `Org_Focus_Fields`) — so a column arriving here can never be org-geo. A
  // gate on isOrgGeoProperty here would always be false and is dead by
  // construction; direct test callers exercise the org-geo strip through
  // stripFieldContext/applyFieldNameMode instead.
  let title = baseTitle;
  const prefix = getFieldContextPrefix(column);
  if (prefix && !title.startsWith(`${prefix}: `)) {
    title = `${prefix}: ${title}`;
  }
  for (const token of getFieldContextSuffixTokens(column)) {
    if (!title.includes(token)) {
      title = `${title} (${token})`;
    }
  }
  return title;
};

/**
 * General-purpose title stripper: exact-match removal (no regex) of the
 * legend prefix (subgroup only — see `getLegendFieldPrefix`, never the
 * category) and every context suffix token — leaving the field name plus its
 * unit ("DML Oct 15 (m)", not "Debug: DML Oct 15 (m)").
 *
 * The unit is deliberately NEVER stripped here (a prior round did this and
 * was reverted — the ticket's "Column 1" example was a unitless field, not an
 * instruction to drop units).
 *
 * Not a true round-trip of decorate(): decorate() skips a suffix token that's
 * already present, strip() always removes it.
 *
 * `applyFieldNameMode` is the only caller today, and only ever invokes this
 * for custom fields — the "Simplify Custom Field Names" toggle it implements
 * is scoped to custom fields only. This function itself has no such scope;
 * it would strip a shapefile/geo/fluid column's context too if called on one.
 */
export const stripFieldContext = (title: string, column: FieldContextColumn): string => {
  if (!title || !column?.property) return title;

  // Scoped to org-geo — see isOrgGeoProperty. Every other field's title is
  // user-authored and must not be substring-matched against its subgroup.
  let stripped = isOrgGeoProperty(column.property)
    ? stripSubgroupFromTitle(title, column.subgroup)
    : title;
  const prefix = getLegendFieldPrefix(column);
  if (prefix && stripped.startsWith(`${prefix}: `)) {
    stripped = stripped.slice(`${prefix}: `.length);
  }
  for (const token of getFieldContextSuffixTokens(column)) {
    stripped = stripped.split(` (${token})`).join("");
  }
  return stripped;
};

/**
 * EVA-127: the one place "Simplify Custom Field Names" is interpreted, so the
 * legend and axis call sites can't drift apart.
 *
 * Scoped to custom fields ONLY (`isCustomFieldProperty`) — shapefile, geo/
 * pad-metrics, and Fluid Analysis fields always take the non-custom branch
 * below regardless of `simplifyCustomFieldNames`, so they render identically
 * whether the toggle is ON, OFF, or the column isn't in a screenshot at all.
 *
 * For custom fields:
 * - `true` (screenshot default, "ON"): strip down to `{field} ({unit})` —
 *   the subgroup is removed, but the unit always survives.
 * - `undefined`/`false` ("OFF" / not-in-screenshot, identical to each other):
 *   prefixed with their SUBGROUP only — the category (e.g. "Organization
 *   Fields") must never reach a legend or axis, in any mode; that's the
 *   EVA-6338 regression this file guards against.
 *
 * The unit is guard-appended for custom fields on every path up front, since
 * Phase 1 dropped it from the stored title.
 *
 * Callers must resolve `?? false` before calling — matching the default, so a
 * stale localStorage blob predating this setting reads as OFF rather than as the
 * opposite of the default.
 *
 * Must stay idempotent: the chart screenshot path applies it twice, once via
 * qualifyAxisName on the live options and again in getChartScreenshotOptions on
 * the copied options.
 */
export const applyFieldNameMode = (
  name: string,
  column: FieldContextColumn,
  simplifyCustomFieldNames?: Maybe<boolean>
): string => {
  if (!name || !column?.property) return name;

  if (!isCustomFieldProperty(column.property)) {
    // decorateTitleWithFieldContext does its own subgroup strip. Running
    // stripSubgroupFromTitle here too — ahead of this check, for every column
    // type — hit org-geo columns as well: getLegendFieldPrefix is empty for
    // them, so nothing re-added the job name the strip removed, and the
    // legend lost it. Scope the strip to custom fields only (below).
    return decorateTitleWithFieldContext(name, column); // toggle is custom-field-only
  }

  // Scoped to org-geo — see isOrgGeoProperty. Every other custom field's title
  // is user-authored and must not be substring-matched against its subgroup.
  let next = isOrgGeoProperty(column.property)
    ? stripSubgroupFromTitle(name, column.subgroup)
    : name;

  // A title persisted before EVA-127 (or rehydrated from sessionStorage,
  // bypassing updateGroupBy — see groupBySlice.ts) can still carry the full
  // legacy `Type: Group: ` category prefix (EVA-6338 shape), not just the
  // subgroup. Strip it up front so neither branch below double-prefixes (OFF)
  // or leaves the category stranded because it doesn't match the subgroup-only
  // prefix `stripFieldContext` looks for (ON).
  const staleCategoryPrefix = getCustomFieldGroupPrefix(column);
  if (staleCategoryPrefix && next.startsWith(`${staleCategoryPrefix}: `)) {
    next = next.slice(staleCategoryPrefix.length + 2);
  }

  // Guard-append the unit Phase 1 dropped from the stored group-by title.
  // The check is "does the title already END in a parenthesised token", not
  // "does it contain literally `(defaultUnit)`": on the AXIS path the title
  // comes from chart-service and its unit can be rendered differently from
  // `defaultUnit` (converted unit system, `bbl/d` vs `bbl`, an abbreviation).
  // An exact-match test misses those and re-creates the very double-unit
  // defect this ticket removes — `Column 1 (bbl/d) (bbl)`. Whatever unit the
  // title already displays is authoritative; never append a second one.
  const alreadyHasUnit = /\([^()]*\)\s*$/.test(next);
  if (column.defaultUnit && !alreadyHasUnit) {
    next = `${next} (${column.defaultUnit})`;
  }

  if (simplifyCustomFieldNames) return stripFieldContext(next, column); // ON

  // OFF / not-in-screenshot
  const subgroupPrefix = getLegendFieldPrefix(column);
  return subgroupPrefix && !next.startsWith(`${subgroupPrefix}: `)
    ? `${subgroupPrefix}: ${next}`
    : next;
};

/**
 * EVA-127 "Simplify Custom Field Names" toggle resolution — the single place
 * that turns "is a screenshot/widget active" + "what does its settings object
 * say" into the `simplifyCustomFieldNames` value `applyFieldNameMode` expects.
 * Five call sites each re-implemented this by hand (one, `useChartScreenshotOptions.ts`,
 * also fell back to a chart entity's `screenshot.properties.preset.simplifyCustomFieldNames`
 * — investigated and confirmed redundant, not dead: `Screenshot.tsx` DOES
 * write `properties.preset` (from `useScreenshotContext().settings`), but
 * that's the SAME settings object this helper already reads via its
 * `settings` param, so the fallback could only ever repeat the value the
 * primary path already resolved, one render later. Dropped here rather than
 * kept, so this helper can't silently diverge from the other four call sites
 * again).
 */
export const resolveSimplifyCustomFieldNames = (
  isScreenshotActive: boolean,
  settings?: Maybe<{ simplifyCustomFieldNames?: Maybe<boolean> }>
): Maybe<boolean> =>
  isScreenshotActive ? settings?.simplifyCustomFieldNames ?? false : undefined;

/**
 * Flat label for space-constrained displays (map hover tooltip, Focus shortcut
 * tiles). Same job as `getFlatDisplayTitle`, with one difference: a custom
 * field is named through `applyFieldNameMode`, so it reads `Group: Field
 * (unit)` and never carries the CATEGORY ("Organization Fields", "My Fields",
 * "Project Fields") — the EVA-127 rule; EVA-339 for the tiles, where the
 * category alone filled the button.
 *
 * The map tooltip-defaults PREVIEW (`DefaultMapTooltipFieldSetting`) uses it
 * too, so it can't drift from what the map renders. Settings PICKERS
 * (`DefaultFocusTiles`, the tooltip-defaults tile) deliberately keep
 * `getFlatDisplayTitle` — the category disambiguates same-named fields across
 * groups there.
 *
 * `simplifyCustomFieldNames` is intentionally not plumbed through: that toggle
 * is a screenshot setting, and none of these displays is captured in one.
 */
export const getCompactFieldDisplayTitle = (
  column: FieldContextColumn & { title?: string },
  labels: ColumnLabelContext,
  fallback = ""
): string => {
  if (!column?.property) return fallback;
  if (isCustomFieldProperty(column.property)) {
    return applyFieldNameMode(column.title ?? "", column) || fallback;
  }
  return getFlatDisplayTitle(column.property, labels, fallback);
};

const getColumnLabelWithContext = (
  property: IGroupByListColumn,
  globalUseNormalizeBy: boolean,
  globalNormalizeBy?: INormalizeBySetting,
  isForecastToggleOn?: boolean,
  selectedForecastFolder?: string,
  entityKind: string = EntityKind.Well,
  performanceDateRange?: { startDate: string; endDate: string }
): ColumnLabelContext => {
  const contextLabels = {
    fullContextTitle: "",
    fullContextTitleWithNormalization: "",
    lightContextTitle: "",
    partialContextTitle: ""
  };

  // Custom fields and shapefile fields often carry an empty `group` on the raw
  // column (the category comes from the property prefix instead), so they must
  // not be caught by the generic empty-group bail-out below.
  const hasRecognizableContext =
    isCustomFieldProperty(property?.property ?? "") ||
    (property?.property ?? "").startsWith("Shapefile_");

  if (!property || (!property.group && !hasRecognizableContext)) {
    return contextLabels;
  }

  // Fluid Analysis columns carry the sampled test ("First"/"Last") in metadata;
  // surface it as "(First Test)" / "(Last Test)". Sourced from the shared
  // tokenizer so the metadata key lives in one place.
  const fluidTestSuffix =
    property.group === FLUID_ANALYSIS_GROUP
      ? getFieldContextSuffixTokens(property)[0] ?? ""
      : "";

  // Performance and Forecast columns read as "{product}: {field}". The BOE ratios are
  // stored without their colon, so it is put back here; this prefix is the one place
  // every context variant below takes the product name from. No other group uses it and
  // getProductLabel walks the whole product list, so geo, pad-metrics, focus and
  // shapefile columns skip the walk.
  const productPrefix =
    property.group === "Forecast" || property.group === "Performance"
      ? getPerformanceRatioDisplayName(getProductLabel(property.product, entityKind))
      : "";

  // Deduped once here so every context variant below reads the same title
  // (see stripSubgroupFromTitle for the org-geo duplication it fixes). Scoped
  // to org-geo columns only — every other column's title is user-authored and
  // must not be substring-matched against its subgroup (see isOrgGeoProperty).
  const fieldTitle = isOrgGeoProperty(property.property)
    ? stripSubgroupFromTitle(property.title, property.subgroup)
    : property.title;
  let title = fieldTitle;
  if (isForecastToggleOn) {
    title = getCumPlusForecastTitle(title, "", property.subgroup);
  }

  // Append date range to title for "Date Range" subgroup fields
  const dateRangeSuffix =
    performanceDateRange && property.subgroup === "Date Range"
      ? ` ${formatPerformanceDateRange(
          performanceDateRange.startDate,
          performanceDateRange.endDate
        )}`
      : "";
  title += dateRangeSuffix;

  // Full Context
  if (property.group === "Forecast") {
    // KNOWN DEFECT (pre-existing, deliberately not fixed under EVA-127 — a
    // behaviour change here would move labels on every fullContextTitle
    // consumer): this guard excludes "" but not nullish, so a caller that
    // omits selectedForecastFolder renders a literal "(undefined)" —
    // "Oil + Cond: Twelve Month Cum (Mbbl) (undefined)". Callers passing
    // `undefined` AND reading fullContextTitle are exposed: getMlFeatureDisplayName.ts,
    // formatMlFeatureName.ts and FeatureSelector.tsx all do, so this is
    // reachable in the ML feature UI for any Forecast-group column. The flat
    // label paths (map tooltip and its preview) read lightContextTitle, which
    // never touches this argument, so they are unaffected either way.
    if (
      selectedForecastFolder !== EVA_RESEARCH_NAME &&
      selectedForecastFolder !== MCDANIEL_SEARCH_FOLDER_NAME &&
      selectedForecastFolder !== ""
    ) {
      contextLabels.fullContextTitle = `${productPrefix}: ${title} (${selectedForecastFolder})`;
      contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
    } else {
      contextLabels.fullContextTitle = `${productPrefix}: ${title}`;
      contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
    }
  } else if (property.group === "Performance") {
    if (
      isForecastToggleOn &&
      selectedForecastFolder !== EVA_RESEARCH_NAME &&
      selectedForecastFolder !== MCDANIEL_SEARCH_FOLDER_NAME
    ) {
      if (property.defaultUnit) {
        contextLabels.fullContextTitle = `${productPrefix}: ${title} (${selectedForecastFolder})`;
        contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
      } else {
        contextLabels.fullContextTitle = `${productPrefix}: ${title} (${selectedForecastFolder})`;
        contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
      }
    } else {
      if (property.defaultUnit) {
        contextLabels.fullContextTitle = `${productPrefix}: ${title}`;
        contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
      } else {
        contextLabels.fullContextTitle = `${productPrefix}: ${title}`;
        contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
      }
    }
  } else if (isCustomFieldProperty(property.property)) {
    // Qualify custom fields as `Type: Group: Field` so same-named fields in
    // different groups can be told apart (Type = category, Group = subgroup).
    const groupPrefix = getFieldContextPrefix(property);
    const prefix = groupPrefix ? `${groupPrefix}: ` : "";
    const unitSuffix = property.defaultUnit ? ` (${property.defaultUnit})` : "";
    contextLabels.fullContextTitle = `${prefix}${fieldTitle}${unitSuffix}`;
    contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
  } else if (property.property.startsWith("Shapefile_")) {
    const shapefilePrefix = getFieldContextPrefix(property);
    contextLabels.fullContextTitle = shapefilePrefix
      ? `${shapefilePrefix}: ${fieldTitle}`
      : fieldTitle;
    contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
  } else if (property.group === FLUID_ANALYSIS_GROUP && fluidTestSuffix) {
    contextLabels.fullContextTitle = `${fieldTitle} (${fluidTestSuffix})`;
    contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
  } else {
    contextLabels.fullContextTitle = `${fieldTitle}`;
    contextLabels.fullContextTitleWithNormalization = contextLabels.fullContextTitle;
  }

  // Full Context with Normalization for Performance fields
  if (property.group === "Performance" && globalNormalizeBy && globalUseNormalizeBy) {
    const fieldUnit = fieldTitle.match(/\((.*?)\)/);
    const unitlessField = title.replace(/\s*\(.*?\)\s*/g, "");
    const unitlessNormalize = globalNormalizeBy.display.replace(/\s*\(.*?\)\s*/g, "");
    const normalizeUnit = globalNormalizeBy.useMultilinearNormalization
      ? " (Normalized)"
      : `/${globalNormalizeBy.per}${globalNormalizeBy.unit}`;
    if (
      isForecastToggleOn &&
      selectedForecastFolder !== EVA_RESEARCH_NAME &&
      selectedForecastFolder !== MCDANIEL_SEARCH_FOLDER_NAME
    ) {
      contextLabels.fullContextTitleWithNormalization = `${productPrefix}: ${unitlessField} (${selectedForecastFolder})/${unitlessNormalize} ${
        fieldUnit ? `${fieldUnit[0]}${normalizeUnit}` : `${normalizeUnit}`
      }`;
    } else {
      contextLabels.fullContextTitleWithNormalization = `${productPrefix}: ${unitlessField}/${unitlessNormalize} ${
        fieldUnit ? `${fieldUnit[0]}${normalizeUnit}` : `${normalizeUnit}`
      }`;
    }
    // Re-append date range suffix since the unitless regex strips all parenthesized content
    if (dateRangeSuffix) {
      contextLabels.fullContextTitleWithNormalization += ` ${dateRangeSuffix.trim()}`;
    }
  } else if (
    isNormalizedProductionSummary(property) &&
    globalNormalizeBy &&
    globalUseNormalizeBy
  ) {
    // A selected field's title carries its display unit, which can differ from defaultUnit.
    const unitlessField = fieldTitle.replace(/\s*\(.*?\)\s*/g, "");
    const unitlessNormalize = globalNormalizeBy.display.replace(/\s*\(.*?\)\s*/g, "");
    const normalizeUnit = globalNormalizeBy.useMultilinearNormalization
      ? " (Normalized)"
      : `/${globalNormalizeBy.per}${globalNormalizeBy.unit}`;
    const fieldUnit =
      fieldTitle.match(/\(.*?\)/)?.[0] ??
      (property.defaultUnit ? `(${property.defaultUnit})` : "");
    contextLabels.fullContextTitleWithNormalization = `${unitlessField}/${unitlessNormalize} ${fieldUnit}${normalizeUnit}`;
  }

  // Light Context
  if (property.group === "Forecast") {
    contextLabels.lightContextTitle = `${title}`;
  } else if (property.group === "Performance") {
    contextLabels.lightContextTitle = `${title}`;
  } else if (isCustomFieldProperty(property.property)) {
    // Light context omits the group name: consumers (e.g. the data-table column
    // picker) already render the group as a section header. The group-qualified
    // name lives in fullContextTitle.
    if (property.defaultUnit) {
      contextLabels.lightContextTitle = `${fieldTitle} (${property.defaultUnit})`;
    } else {
      contextLabels.lightContextTitle = `${fieldTitle}`;
    }
  } else if (property.property.startsWith("Shapefile_")) {
    const shapefilePrefix = getFieldContextPrefix(property);
    contextLabels.lightContextTitle = shapefilePrefix
      ? `${shapefilePrefix}: ${fieldTitle}`
      : fieldTitle;
  } else if (property.group === FLUID_ANALYSIS_GROUP && fluidTestSuffix) {
    contextLabels.lightContextTitle = `${fieldTitle} (${fluidTestSuffix})`;
  } else {
    contextLabels.lightContextTitle = `${fieldTitle}`;
  }

  // Partial Context
  if (property.group === "Forecast") {
    contextLabels.partialContextTitle = `${productPrefix}: ${title}`;
  } else if (property.group === "Performance") {
    contextLabels.partialContextTitle = `${productPrefix}: ${title}`;
  } else if (
    property.group === geoVolumeCategoryTitle ||
    property.group === padMetricsCategoryTitle
  ) {
    contextLabels.partialContextTitle = `${fieldTitle}`;
  } else if (isCustomFieldProperty(property.property)) {
    // Same "Type: Group:" qualification as fullContextTitle, so consumers that
    // read partialContextTitle (e.g. the filter predicate tree) show the group.
    const groupPrefix = getFieldContextPrefix(property);
    const prefix = groupPrefix ? `${groupPrefix}: ` : "";
    contextLabels.partialContextTitle = `${prefix}${fieldTitle}`;
  } else if (property.property.startsWith("Shapefile_")) {
    const shapefilePrefix = getFieldContextPrefix(property);
    contextLabels.partialContextTitle = shapefilePrefix
      ? `${shapefilePrefix}: ${fieldTitle}`
      : fieldTitle;
  } else if (property.group === FLUID_ANALYSIS_GROUP && fluidTestSuffix) {
    contextLabels.partialContextTitle = `${fieldTitle} (${fluidTestSuffix})`;
  }

  // Append geo/pad-metrics radius + source to every non-empty label. Sourced
  // from the shared tokenizer so the "which tokens does a geo field carry" rule
  // lives in one place (also used by decorateTitleWithFieldContext). Radius is
  // keyed off its presence, not metadata.type ("Summary"/"Statistics"/…).
  if (
    property.group === geoVolumeCategoryTitle ||
    property.group === padMetricsCategoryTitle
  ) {
    for (const token of getFieldContextSuffixTokens(property)) {
      if (!contextLabels.fullContextTitle.includes(token)) {
        contextLabels.fullContextTitle = `${contextLabels.fullContextTitle} (${token})`;
        contextLabels.fullContextTitleWithNormalization = `${contextLabels.fullContextTitleWithNormalization} (${token})`;
        contextLabels.partialContextTitle = `${contextLabels.partialContextTitle} (${token})`;
        contextLabels.lightContextTitle = `${contextLabels.lightContextTitle} (${token})`;
      }
    }
  }

  return contextLabels;
};

export default getColumnLabelWithContext;
